// @ts-nocheck
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { invokeEdgeFunction } from '@/lib/invokeEdgeFunction';

/**
 * The coach/customer view of "what am I registered for, and what do I owe".
 *
 * Every screen that shows a balance reads it from here. The figure comes from
 * rle-list-my-billing, which computes it with the same shared function
 * (_shared/billing-balance.ts) that rle-create-billing-checkout uses to decide
 * what Stripe is asked to charge — so the number on the button and the number
 * charged cannot drift.
 *
 * Never re-derive a balance in a component.
 */
export interface MyBillingRegistration {
  id: string;
  event_id: string;
  event_name: string;
  event_slug: string | null;
  event_start_date: string | null;
  event_end_date: string | null;
  event_venue: string | null;
  event_city: string | null;
  is_demo_event: boolean;
  team_name: string;
  division: string | null;
  pool: string | null;
  registration_status: string | null;
  payment_status: string | null;
  payment_method: string | null;
  total_amount_due: number;
  total_amount_paid: number;
  total_amount_refunded: number;
  extras_total: number;
  discount_total: number;
  outstanding_balance: number;
  can_pay: boolean;
  my_role: 'coach' | 'billing_contact' | 'registrant';
  created_at: string;
  extras: Array<{
    id: string;
    extra_name: string;
    extra_price: number;
    payment_status: string | null;
    player_name: string | null;
    team_name: string | null;
    qr_code: string | null;
    is_redeemed: boolean | null;
  }>;
}

export interface MyBillingSummary {
  count: number;
  total_due: number;
  total_paid: number;
  total_outstanding: number;
}

export function useMyBilling(options: { onlyOutstanding?: boolean; eventId?: string } = {}) {
  const { user } = useAuth();
  const { onlyOutstanding = false, eventId } = options;

  return useQuery({
    queryKey: ['my-billing', user?.id ?? null, onlyOutstanding, eventId ?? null],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data, error } = await invokeEdgeFunction('rle-list-my-billing', {
        body: {
          only_outstanding: onlyOutstanding,
          ...(eventId ? { event_id: eventId } : {}),
        },
      });
      if (error) throw error;
      return {
        registrations: (data?.data?.registrations ?? []) as MyBillingRegistration[],
        summary: (data?.data?.summary ?? {
          count: 0,
          total_due: 0,
          total_paid: 0,
          total_outstanding: 0,
        }) as MyBillingSummary,
      };
    },
  });
}

export const formatMoney = (amount: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(amount || 0));
